import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { Router } from "@angular/router";
import {
  AssinaturaService,
  AutorizacaoAssinatura,
  ConsultaAssinatura,
  ContratoAssinatura,
} from "src/app/core/services/assinatura.service";
import { ToastNotificationService } from "src/app/components/toast-notification/toast-notification.service";

export type PassoCheckoutAssinatura = "contrato" | "checkout";

@Component({
  selector: "app-checkout-assinatura",
  templateUrl: "./checkout-assinatura.component.html",
  styleUrls: ["./checkout-assinatura.component.css"],
})
export class CheckoutAssinaturaComponent implements OnChanges {
  @Input() passo: PassoCheckoutAssinatura = "contrato";
  @Input() pedidoId: number;
  @Output() passoChange = new EventEmitter<PassoCheckoutAssinatura>();

  consulta: ConsultaAssinatura | null = null;
  contratoHtml: SafeHtml | null = null;
  private htmlContrato = "";
  erroStatus = "";
  carregando = false;
  linkCopiado = false;
  confirmarAutorizacao = false;
  motivoAutorizacao = "";

  constructor(
    private router: Router,
    private assinaturaService: AssinaturaService,
    private sanitizer: DomSanitizer,
    private toastNotification: ToastNotificationService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes["pedidoId"] || changes["passo"]) &&
      this.passo === "contrato" &&
      this.pedidoId
    ) {
      this.carregar();
    }
  }

  get status(): string | null {
    return this.consulta?.contrato?.status || null;
  }

  get rotulo(): string {
    if (!this.status || this.status === "PENDENTE") {
      return "Pendente";
    }
    return AssinaturaService.rotuloStatus(this.status);
  }

  get badge(): string {
    switch (this.status) {
      case "AGUARDANDO_ASSINATURA":
        return "AGUARDANDO ASSINATURA";
      case "ASSINADO":
        return "ASSINADO";
      case "AUTORIZADO":
        return "DISPENSADO";
      case "RECUSADO":
        return "RECUSADO";
      case "EXPIRADO":
        return "EXPIRADO";
      case "ERRO":
        return "ERRO";
      case "CANCELADO":
        return "CANCELADO";
      default:
        return "PENDENTE";
    }
  }

  get textoStatus(): string {
    switch (this.status) {
      case "AGUARDANDO_ASSINATURA":
        return "O contrato foi enviado para a Autentique. Aguarde a assinatura do signatário.";
      case "ASSINADO":
        return "Assinatura confirmada. O checkout está liberado.";
      case "AUTORIZADO":
        return this.textoAutorizado();
      case "RECUSADO":
        return "O signatário recusou a assinatura.";
      case "EXPIRADO":
        return "O prazo de assinatura encerrou.";
      case "ERRO":
        return "Não foi possível enviar o contrato.";
      case "CANCELADO":
        return "O contrato foi cancelado. Solicite uma nova assinatura.";
      default:
        return "O contrato ainda não foi enviado para assinatura.";
    }
  }

  get textoAcaoPrincipal(): string {
    if (this.status === "AGUARDANDO_ASSINATURA") {
      return "Abrir assinatura";
    }
    if (this.status === "ASSINADO") {
      return "Ver documento assinado";
    }
    if (this.status === "AUTORIZADO") {
      return "Assinatura dispensada";
    }
    return "Assinar contrato";
  }

  get dicaAcaoPrincipal(): string {
    if (this.status === "AGUARDANDO_ASSINATURA") {
      return "Abre o link da Autentique em uma nova aba.";
    }
    if (this.status === "ASSINADO") {
      return "Abre o documento confirmado pela Autentique.";
    }
    if (this.status === "AUTORIZADO") {
      return "A dispensa precisa ser revertida para iniciar uma assinatura.";
    }
    return "Envia o contrato à Autentique e abre o link de assinatura.";
  }

  get podeIrCheckout(): boolean {
    if (this.consulta && !this.consulta.contrato_obrigatorio) {
      return true;
    }
    return this.status === "ASSINADO" || this.status === "AUTORIZADO";
  }

  get acaoPrincipalDesabilitada(): boolean {
    return this.carregando || this.status === "AUTORIZADO";
  }

  get enviadoEm(): string {
    const contrato = this.consulta?.contrato as any;
    return this.formatarData(contrato?.enviado_em || contrato?.criado_em);
  }

  get signatario(): string {
    const contrato = this.consulta?.contrato as any;
    return contrato?.signatario || contrato?.nome_signatario || "—";
  }

  get emailSignatario(): string {
    const contrato = this.consulta?.contrato as any;
    return contrato?.email_signatario || contrato?.email || "";
  }

  voltarCatalogo() {
    this.router.navigate(["/catalogo"]);
  }

  irPara(passo: PassoCheckoutAssinatura) {
    if (passo === "checkout") {
      this.tentarCheckout();
      return;
    }
    this.passoChange.emit(passo);
  }

  tentarCheckout() {
    if (!this.podeIrCheckout) {
      this.toastNotification.warning(
        "Para continuar com o pedido, assine o contrato comercial ou solicite autorização para prosseguir sem assinatura.",
        "Assinatura do contrato obrigatória"
      );
      return;
    }
    this.passoChange.emit("checkout");
  }

  carregar() {
    this.assinaturaService.consultar(this.pedidoId, false).subscribe({
      next: (consulta) => {
        this.consulta = consulta;
        this.erroStatus = "";
        if (!consulta.contrato_obrigatorio && this.passo === "contrato") {
          this.tentarCheckout();
          return;
        }
        if (!this.contratoHtml) {
          this.visualizar();
        }
      },
      error: (error) => {
        this.erroStatus = AssinaturaService.mensagem(error);
      },
    });
  }

  visualizar() {
    if (!this.pedidoId) return;
    this.carregando = true;
    this.assinaturaService.visualizar(this.pedidoId).subscribe({
      next: (html) => {
        this.carregando = false;
        this.htmlContrato = html;
        this.contratoHtml = this.sanitizer.bypassSecurityTrustHtml(html);
      },
      error: (error) => {
        this.carregando = false;
        this.erroStatus = AssinaturaService.mensagem(error);
      },
    });
  }

  baixar() {
    const url = this.consulta?.contrato?.url_documento_assinado;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    if (!this.htmlContrato) {
      this.visualizar();
      return;
    }
    const arquivo = new Blob([this.htmlContrato], { type: "text/html" });
    const arquivoUrl = URL.createObjectURL(arquivo);
    window.open(arquivoUrl, "_blank", "noopener,noreferrer");
  }

  acaoPrincipal() {
    if (this.status === "AUTORIZADO") {
      return;
    }
    if (this.status === "ASSINADO") {
      this.baixar();
      return;
    }
    if (this.status === "AGUARDANDO_ASSINATURA") {
      const url = this.consulta?.contrato?.url_assinatura;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
    }
    this.solicitarAssinatura(true);
  }

  copiarLink() {
    const url = this.consulta?.contrato?.url_assinatura;
    if (!url) {
      return;
    }
    this.copiar(url);
  }

  verLink() {
    const url = this.consulta?.contrato?.url_assinatura;
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  verificar() {
    if (!this.pedidoId) return;
    this.carregando = true;
    this.erroStatus = "";
    this.assinaturaService.consultar(this.pedidoId, true).subscribe({
      next: (consulta) => {
        this.carregando = false;
        this.consulta = consulta;
      },
      error: (error) => {
        this.carregando = false;
        this.erroStatus = AssinaturaService.mensagem(error);
      },
    });
  }

  abrirAutorizacao() {
    if (this.contratoJaLiberado()) {
      this.avisarContratoAutorizado();
      return;
    }
    this.motivoAutorizacao = "";
    this.erroStatus = "";
    this.confirmarAutorizacao = true;
  }

  cancelarAutorizacao() {
    this.confirmarAutorizacao = false;
    this.motivoAutorizacao = "";
  }

  confirmarDispensa() {
    if (this.contratoJaLiberado()) {
      this.confirmarAutorizacao = false;
      this.avisarContratoAutorizado();
      return;
    }
    const motivo = this.motivoAutorizacao.trim();
    if (!motivo || !this.pedidoId || this.carregando) return;
    this.carregando = true;
    this.erroStatus = "";
    this.assinaturaService.autorizar(this.pedidoId, motivo).subscribe({
      next: (resp) => {
        this.carregando = false;
        this.confirmarAutorizacao = false;
        this.motivoAutorizacao = "";
        this.aplicarAutorizacao(resp);
      },
      error: (error) => {
        this.carregando = false;
        this.erroStatus = AssinaturaService.mensagem(error);
      },
    });
  }

  private solicitarAssinatura(abrir: boolean) {
    if (!this.pedidoId) return;
    this.carregando = true;
    this.erroStatus = "";
    this.assinaturaService.solicitar(this.pedidoId).subscribe({
      next: (resp) => {
        this.carregando = false;
        this.aplicarSolicitacao(resp.status, resp.url_assinatura);
        if (!resp.url_assinatura) {
          this.erroStatus =
            "A Autentique não devolveu o link de assinatura. Tente novamente.";
          return;
        }
        if (abrir) {
          window.open(resp.url_assinatura, "_blank", "noopener,noreferrer");
        }
      },
      error: (error) => {
        this.carregando = false;
        if (error?.error?.detail?.codigo === "ASSINATURA_NAO_OBRIGATORIA") {
          this.tentarCheckout();
          return;
        }
        this.erroStatus = AssinaturaService.mensagem(error);
      },
    });
  }

  private contratoJaLiberado(): boolean {
    return this.status === "ASSINADO" || this.status === "AUTORIZADO";
  }

  private avisarContratoAutorizado() {
    this.toastNotification.warning("Contrato já Autorizado.");
  }

  private textoAutorizado(): string {
    const quem = this.nomePessoa(this.consulta?.contrato?.autorizado_por);
    const quando = this.consulta?.contrato?.autorizado_em
      ? this.formatarData(this.consulta.contrato.autorizado_em)
      : "";
    if (quem && quando && quando !== "—") {
      return `Pedido dispensado de assinatura por ${quem} em ${quando}. O checkout está liberado.`;
    }
    return "Pedido dispensado de assinatura. O checkout está liberado.";
  }

  private nomePessoa(valor: ContratoAssinatura["autorizado_por"]): string {
    if (!valor) return "";
    if (typeof valor === "string") return valor;
    return valor.nome || valor.name || "";
  }

  private aplicarAutorizacao(resp: AutorizacaoAssinatura | null) {
    const atual = this.consulta;
    const contratoResp = resp?.contrato ?? null;
    const status = contratoResp?.status || resp?.status || "AUTORIZADO";
    const contrato: ContratoAssinatura = {
      id: contratoResp?.id || atual?.contrato?.id || 0,
      status,
      nome_documento:
        contratoResp?.nome_documento ||
        atual?.contrato?.nome_documento ||
        `Contrato do pedido ${this.pedidoId}`,
      url_assinatura:
        contratoResp?.url_assinatura ?? atual?.contrato?.url_assinatura ?? null,
      url_documento_assinado:
        contratoResp?.url_documento_assinado ??
        atual?.contrato?.url_documento_assinado ??
        null,
      assinado_em:
        contratoResp?.assinado_em ?? atual?.contrato?.assinado_em ?? null,
      hash_documento:
        contratoResp?.hash_documento ?? atual?.contrato?.hash_documento ?? null,
      autorizado_por:
        contratoResp?.autorizado_por ?? resp?.autorizado_por ?? null,
      autorizado_em:
        contratoResp?.autorizado_em ?? resp?.autorizado_em ?? null,
    };
    this.consulta = {
      pedido_id: resp?.pedido_id || atual?.pedido_id || this.pedidoId,
      contrato_obrigatorio:
        resp?.contrato_obrigatorio ?? atual?.contrato_obrigatorio ?? true,
      permite_finalizar: resp?.permite_finalizar ?? status === "AUTORIZADO",
      contrato,
    };
  }

  private aplicarSolicitacao(status: string, url: string | null) {
    this.consulta = {
      pedido_id: this.consulta?.pedido_id || this.pedidoId,
      contrato_obrigatorio: this.consulta?.contrato_obrigatorio ?? true,
      permite_finalizar: false,
      contrato: {
        id: this.consulta?.contrato?.id || 0,
        status,
        nome_documento:
          this.consulta?.contrato?.nome_documento ||
          `Contrato do pedido ${this.pedidoId}`,
        url_assinatura: url,
        url_documento_assinado:
          this.consulta?.contrato?.url_documento_assinado || null,
        assinado_em: null,
        hash_documento: this.consulta?.contrato?.hash_documento || null,
      },
    };
    this.linkCopiado = false;
  }

  private copiar(url: string) {
    if (!navigator.clipboard) {
      this.erroStatus = url;
      return;
    }
    navigator.clipboard.writeText(url).then(() => {
      this.linkCopiado = true;
    });
  }

  private formatarData(valor: string | null | undefined): string {
    if (!valor) return "—";
    const data = new Date(valor);
    if (Number.isNaN(data.getTime())) return valor;
    return data.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
}
